// Libraries
import { useCallback, useState } from "react";

// Context
import { useDataControl } from "@/Common/context/dataControl";
import { useCRMContext } from "@/DataPages/context/crm/CRMContext";
import { LeadsContextProvider } from "@/DataPages/context/crm/LeadsContext";

// Helpers
import { isCustomersActiveTab } from "@/DataPages/helpers/crm/utils";

// Components
import LeadDispositions from "./LeadDispositions";
import SalesLeads from "./SalesLeads";
import Customers from "./Customers";
import SocialMediaReviews from "./SocialMediaReviews";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";

// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";

// Constants
import ACTIONS from "@/Constants/actionsList";

const REQUIRED_ACTIONS = [
	ACTIONS.VIEW_SALES_LEADS,
	ACTIONS.VIEW_DISPOSITIONS,
	ACTIONS.VIEW_SALES_LEAD_CUSTOMERS,
	ACTIONS.VIEW_SOCIAL_MEDIA_REVIEWS,
];

const CONTENT_TABS = [
	(isActive: boolean) => (
		<LeadsContextProvider key={"Leads"}>
			<SalesLeads isActiveTab={isActive} />
		</LeadsContextProvider>
	),
	(isActive: boolean) => <LeadDispositions key={"Dispositions"} isActiveTab={isActive} />,
	(isActive: boolean) => <Customers key={"Customers"} isActiveTab={isActive} />,
	(isActive: boolean) => <SocialMediaReviews key={"Social Media Reviews"} isActiveTab={isActive} />,
];

type TabInfo = {
	title: string;
	index: number;
	disabled: boolean;
};

export default function CRMContainer() {
	const { tabs: contextTabs = [], setTabSelected: setTabSelectedContext } = useCRMContext();

	const { setData: setControlData } = useDataControl();

	const { useAuth } = useAuthorization();

	const [hasLeadAccess, hasDispositionAccess, hasCustomerAccess, hasSocialMediaReviewAccess] = REQUIRED_ACTIONS.map(useAuth);

	const isTabEnabled = useCallback(
		(key: string): boolean => {
			switch (key) {
				case "leads":
					return hasLeadAccess;
				case "dispositions":
					return hasDispositionAccess;
				case "customers":
					return hasCustomerAccess;
				case "social-media-reviews":
					return hasSocialMediaReviewAccess;
				default:
					return false;
			}
		},
		[hasLeadAccess, hasDispositionAccess, hasCustomerAccess, hasSocialMediaReviewAccess]
	);

	const tabs = contextTabs.map(({ key, label }, index) => ({ title: label, index, disabled: !isTabEnabled(key) }));
	const firstTabWithAccess = tabs.find((tab) => !tab.disabled) ?? { title: tabs?.[0]?.title ?? "", index: 0, disabled: true };
	const [tabSelected, setTabSelected] = useState<TabInfo>(() => ({ ...firstTabWithAccess }));

	function onTabChange(_: number, tabInfo: { label: TabInfo; index: number }) {
		setTabSelected(tabInfo.label);
		setTabSelectedContext?.(tabInfo.index);

		const isActiveCustomerTab = isCustomersActiveTab(tabInfo.index, contextTabs);

		(setControlData as (value: unknown) => void)((prevData: object) => ({
			...prevData,
			panelDisabled: isActiveCustomerTab,
		}));
	}

	return (
		<TabSelectable
			tabs={tabs}
			tabBarClassName={"!p-0 !m-0 !pb-8"}
			tabsProps={{
				tabClasses: "!min-w-[1100px]",
			}}
			content={CONTENT_TABS.map((content, index) => content(tabSelected.index === index))}
			tabSelected={tabSelected.index}
			onTabChange={onTabChange}
		/>
	);
}
